<template>
  <AdminLayout>
    <Head title="Company Users" />

    <div class="headline flex flex-row justify-between mx-4 sm:mx-12">
      <h2 class="text-2xl font-semibold pb-5 pt-5 text-blue-600">Edit User</h2>

      <div class="relative inline-flex shadow-sm rounded-md self-start mt-2">
        <button
          @click.prevent="submit()"
          :disabled="form.processing"
          type="button"
          class="inline-flex items-center px-4 py-3 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
        >
          Save Changes
        </button>
        <button
          @click.prevent="deleteUser()"
          :disabled="form.processing"
          type="button"
          class="inline-flex items-center px-4 py-3 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-red-600 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600"
        >
          Delete User
        </button>
      </div>
    </div>

    <div
      v-if="$page.props.flash.success"
      class="alert alert-success"
    >
      <p class="alert-text">
        {{ $page.props.flash.success }}
      </p>
    </div>

    <div class="form bg-gray-100 flex-1 py-10 px-4 sm:px-12">
      <form
        method="POST"
        @submit.prevent="submit()"
      >
        <div class="grid grid-cols-12 gap-6">
          <div class="input-group col-span-12 sm:col-span-6 lg:col-span-4">
            <MultiSelectInput
              v-model="form.roles"
              :options="roleOptions"
            >
              Roles
            </MultiSelectInput>
            <p
              v-if="getErrors('roles')"
              class="input-error"
            >
              {{ getErrors('roles') }}
            </p>
          </div>

          <div class="input-group col-span-12 sm:col-span-6 lg:col-span-4">
            <label for="first_name"> First Name </label>
            <input
              type="text"
              v-model="form.first_name"
              name="first_name"
              id="first_name"
              autocomplete="first_name"
            />
            <p
              v-if="getErrors('first_name')"
              class="input-error"
            >
              {{ getErrors('first_name') }}
            </p>
          </div>

          <div class="input-group col-span-12 sm:col-span-6 lg:col-span-4">
            <label for="last_name"> Last Name </label>
            <input
              type="text"
              v-model="form.last_name"
              name="last_name"
              id="last_name"
              autocomplete="last_name"
            />
            <p
              v-if="getErrors('last_name')"
              class="input-error"
            >
              {{ getErrors('last_name') }}
            </p>
          </div>

          <div class="input-group col-span-12 sm:col-span-6 lg:col-span-4">
            <label for="birthdate"> Birthdate </label>
            <DatePicker
              v-model="form.birthdate"
              color="gray"
              title-position="right"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <div class="relative">
                  <CalendarIcon
                    class="top-3 left-4 h-5 w-5 text-gray-800 absolute"
                    aria-hidden="true"
                  />
                  <input
                    class="inline-flex items-center w-40 justify-center pl-10 border-2 border-gray-400 rounded-lg px-4 h-[42px] bg-gray-100 text-sm font-medium text-gray-800 cursor-pointer"
                    id="birthdate"
                    :value="inputValue"
                    v-on="inputEvents"
                    autocomplete="birthdate"
                  />
                </div>
              </template>
            </DatePicker>
            <p
              v-if="getErrors('birthdate')"
              class="input-error"
            >
              {{ getErrors('birthdate') }}
            </p>
          </div>

          <div class="input-group col-span-6 sm:col-span-6 lg:col-span-6">
            <label for="phone"> Phone </label>
            <input
              type="text"
              v-model="form.phone"
              name="phone"
              id="phone"
            />
            <p
              v-if="getErrors('phone')"
              class="input-error"
            >
              {{ getErrors('phone') }}
            </p>
          </div>

          <div class="input-group col-span-6 sm:col-span-6 lg:col-span-6">
            <label for="email"> Email </label>
            <input
              type="text"
              v-model="form.email"
              name="email"
              id="email"
            />
            <p
              v-if="getErrors('email')"
              class="input-error"
            >
              {{ getErrors('email') }}
            </p>
          </div>

          <div class="input-group col-span-6 sm:col-span-6 lg:col-span-6">
            <label for="password"> Password </label>
            <input
              type="text"
              v-model="form.password"
              name="password"
              id="password"
            />
            <p
              v-if="getErrors('password')"
              class="input-error"
            >
              {{ getErrors('password') }}
            </p>
          </div>

          <div class="input-group col-span-6 sm:col-span-6 lg:col-span-6">
            <label for="password_confirmation"> Confirm Password </label>
            <input
              type="text"
              v-model="form.password_confirmation"
              name="password_confirmation"
              id="password_confirmation"
            />
            <p
              v-if="getErrors('password_confirmation')"
              class="input-error"
            >
              {{ getErrors('password_confirmation') }}
            </p>
          </div>

          <div class="input-group col-span-6 sm:col-span-6 lg:col-span-6">
            <label for="created_at"> Creation Date </label>
            <input
              type="text"
              disabled
              :value="formatDate(form.created_at)"
              name="created_at"
              id="created_at"
            />
          </div>

          <div class="input-group col-span-6 sm:col-span-6 lg:col-span-6">
            <label for="updated_at"> Updated Date </label>
            <input
              type="text"
              disabled
              :value="formatDate(form.updated_at)"
              name="updated_at"
              id="updated_at"
            />
          </div>
        </div>
      </form>

      <hr class="my-6" />

      <h3>Driver Information</h3>

      <div class="grid grid-cols-12 gap-6">
        <div class="input-group col-span-12 sm:col-span-6 lg:col-span-4">
          <label for="drivers_license_number"> Driver's License Number </label>
          <input
            type="text"
            v-model="form.drivers_license_number"
            name="drivers_license_number"
            id="drivers_license_number"
            autocomplete="drivers_license_number"
          />
          <p
            v-if="getErrors('drivers_license_number')"
            class="input-error"
          >
            {{ getErrors('drivers_license_number') }}
          </p>
        </div>

        <div class="input-group col-span-12 sm:col-span-6 lg:col-span-4">
          <label for="state_issuer"> State Issuer </label>
          <select
            v-model="form.state_issuer"
            name="state_issuer"
            id="state_issuer"
            autocomplete="state_issuer"
          >
            <option value="">Select a state</option>
            <option
              v-for="state in stateOptions"
              :value="state.value"
            >
              {{ state.label }}
            </option>
          </select>
          <p
            v-if="getErrors('state_issuer')"
            class="input-error"
          >
            {{ getErrors('state_issuer') }}
          </p>
        </div>
      </div>

      <hr class="my-6" />

      <!-- Settings -->
      <h3>Settings</h3>

      <div class="grid grid-cols-12 gap-4 mt-4">
        <div class="input-group-checkbox col-span-12">
          <div class="input-checkbox-container">
            <input
              v-model="form.has_low_stock_notifications"
              id="has_low_stock_notifications"
              name="has_low_stock_notifications"
              type="checkbox"
            />
          </div>
          <div class="input-label-container">
            <label for="has_low_stock_notifications"> Receives low stock notifications </label>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-4 mt-4">
        <div class="input-group-checkbox col-span-12">
          <div class="input-checkbox-container">
            <input
              v-model="form.has_delivery_max_notifications"
              id="has_delivery_max_notifications"
              name="has_delivery_max_notifications"
              type="checkbox"
            />
          </div>
          <div class="input-label-container">
            <label for="has_delivery_max_notifications"> Receives delivery max notifications </label>
          </div>
        </div>
      </div>
      <!-- END Settings -->
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import dateFilters from '@/Mixins/dateFilters'
import { useConfirmDialog } from '@/Composables/confirm-dialog'
import MultiSelectInput from '@/Components/Forms/MultiSelectInput.vue'
import { DatePicker } from 'v-calendar'
import { CalendarIcon } from '@heroicons/vue/outline'

export default {
  mixins: [dateFilters],

  components: {
    AdminLayout,
    Head,
    Link,
    MultiSelectInput,
    DatePicker,
    CalendarIcon
  },

  props: {
    user: Object,
    errors: Object,
    roles: Array,
    states: Object
  },

  data() {
    return {
      form: {
        roles: [],
        first_name: '',
        last_name: '',
        birthdate: '',
        phone: '',
        email: '',
        drivers_license_number: '',
        state_issuer: '',
        password: '',
        password_confirmation: '',
        has_low_stock_notifications: false,
        has_delivery_max_notifications: false
      }
    }
  },

  created() {
    console.log('created()', this.user, this.errors)

    this.form = useForm({
      ...this.form,
      ...(this.user.data || {})
    })
  },

  computed: {
    roleOptions() {
      return Object.keys(this.roles).map((roleKey) => ({ label: this.roles[roleKey], value: roleKey }))
    },
    stateOptions() {
      return Object.keys(this.states).map((stateKey) => ({ label: this.states[stateKey], value: stateKey }))
    }
  },

  methods: {
    submit() {
      this.form
        .transform((data) => {
          if (!data.password) {
            delete data.password
          }

          return data
        })
        .put(this.route('company.users.update', { user: this.user.data.id }), {
          onSuccess: () => {
            this.form.reset('password')
            this.form.reset('password_confirmation')
          }
        })
    },
    getErrors(field) {
      if (field in this.errors) {
        return this.errors[field]
      }

      return ''
    },
    async deleteUser() {
      const { show } = useConfirmDialog()
      try {
        await show({
          type: 'danger',
          title: 'Delete User',
          body: 'Are you sure you want to delete this user?'
        })

        useForm({}).delete(route('company.users.destroy', { user: this.user.data.id }))
      } catch (e) {
        console.log(e)
      }
    }
  }
}
</script>
